<template>
  <div class="crm-tasks-page">
    <section class="crm-tasks-panel">
      <header class="crm-tasks-header">
        <div>
          <h1>Tarefas</h1>
          <span>{{ pagination.total }} registros encontrados</span>
        </div>
        <button
          class="btn btn-dark font-weight-bolder font-size-sm"
          @click="abrirModal"
        >
          Nova tarefa
        </button>
      </header>

      <div class="crm-tasks-filters">
        <b-form-input
          v-model="filtros.busca"
          placeholder="Buscar por tarefa, oportunidade ou contato"
          @input="debouncedLoadAtividades"
        />
        <b-form-select
          v-model="filtros.status"
          :options="statusFilterOptions"
          @change="trocarFiltroStatus"
        />
        <button
          class="btn btn-light btn-sm"
          type="button"
          :disabled="loading"
          @click="loadAtividades"
        >
          Atualizar
        </button>
      </div>

      <div class="crm-tasks-table-wrap">
        <table class="crm-tasks-table">
          <thead>
            <tr>
              <th>Tarefa</th>
              <th>Status</th>
              <th>Data e hora</th>
              <th>Responsável</th>
              <th>Negociação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="5" class="crm-tasks-empty">Carregando tarefas...</td>
            </tr>
            <tr v-else-if="!tarefas.length">
              <td colspan="5" class="crm-tasks-empty">
                Nenhuma tarefa encontrada
              </td>
            </tr>
            <tr
              v-for="tarefa in tarefas"
              v-else
              :key="tarefa.id_crm_atividade"
              class="crm-task-row"
              :class="{ 'is-late': isLate(tarefa) }"
              @click="abrirDrawer(tarefa)"
            >
              <td>
                <div class="crm-task-title-cell">
                  <i class="la la-check-square"></i>
                  <div>
                    <strong>{{ tarefa.titulo }}</strong>
                    <span>{{ tipoTexto(tarefa.tipo) }}</span>
                  </div>
                </div>
              </td>
              <td>
                <span class="crm-task-status" :class="statusClass(tarefa)">
                  {{ statusTexto(tarefa) }}
                </span>
              </td>
              <td>{{ formatDateTime(tarefa.vencimento_em) }}</td>
              <td>{{ tarefa.usuario || "Sem responsável" }}</td>
              <td>
                <button
                  v-if="tarefa.id_crm_oportunidade"
                  class="crm-task-link"
                  type="button"
                  @click.stop="abrirOportunidade(tarefa)"
                >
                  {{ tarefa.oportunidade || "Abrir oportunidade" }}
                </button>
                <span v-else>{{ tarefa.contato || "Sem oportunidade" }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer class="crm-tasks-footer">
        <b-form-select
          v-model="pagination.perPage"
          :options="perPageOptions"
          class="crm-tasks-per-page"
          @change="trocarPorPagina"
        />
        <div class="crm-tasks-pages">
          <button
            class="btn btn-light btn-sm"
            type="button"
            :disabled="loading || pagination.page <= 1"
            @click="trocarPagina(pagination.page - 1)"
          >
            Voltar
          </button>
          <span>Página {{ pagination.page }} de {{ lastPage }}</span>
          <button
            class="btn btn-light btn-sm"
            type="button"
            :disabled="loading || pagination.page >= lastPage"
            @click="trocarPagina(pagination.page + 1)"
          >
            Próximo
          </button>
        </div>
      </footer>
    </section>

    <b-sidebar
      v-model="drawerVisible"
      backdrop
      right
      shadow
      title="Detalhes da tarefa"
      width="460px"
    >
      <div v-if="tarefaSelecionada" class="crm-task-drawer">
        <div class="crm-task-drawer-top">
          <span class="crm-task-status" :class="statusClass(tarefaSelecionada)">
            {{ statusTexto(tarefaSelecionada) }}
          </span>
          <div class="crm-task-drawer-actions">
            <button
              v-if="canEditTarefa(tarefaSelecionada) && !drawerEditando"
              class="btn btn-light btn-sm"
              type="button"
              @click="iniciarEdicaoDrawer"
            >
              Editar
            </button>
            <button
              v-if="drawerEditando"
              class="btn btn-light btn-sm"
              type="button"
              :disabled="loadingDrawerSave"
              @click="cancelarEdicaoDrawer"
            >
              Cancelar
            </button>
          </div>
        </div>

        <template v-if="drawerEditando">
          <b-form-group label="Título">
            <b-form-input v-model="drawerForm.titulo" />
          </b-form-group>
          <b-form-group label="Tipo">
            <b-form-select v-model="drawerForm.tipo" :options="tipos" />
          </b-form-group>
          <b-form-group label="Responsável">
            <b-form-select
              v-model="drawerForm.id_usuario"
              :options="usuarioOptionsSemTodos"
            />
          </b-form-group>
          <b-form-group label="Data e hora">
            <b-form-input
              v-model="drawerForm.vencimento_em"
              type="datetime-local"
            />
          </b-form-group>
          <b-form-group label="Descrição">
            <b-form-textarea v-model="drawerForm.descricao" rows="4" />
          </b-form-group>
          <button
            class="btn btn-dark btn-block"
            type="button"
            :disabled="loadingDrawerSave"
            @click="salvarEdicaoDrawer"
          >
            Salvar alterações
          </button>
        </template>

        <template v-else>
          <h2>{{ tarefaSelecionada.titulo }}</h2>
          <dl>
            <div>
              <dt>Tipo</dt>
              <dd>{{ tipoTexto(tarefaSelecionada.tipo) }}</dd>
            </div>
            <div>
              <dt>Data e hora</dt>
              <dd>{{ formatDateTime(tarefaSelecionada.vencimento_em) }}</dd>
            </div>
            <div>
              <dt>Responsável</dt>
              <dd>{{ tarefaSelecionada.usuario || "Sem responsável" }}</dd>
            </div>
            <div>
              <dt>Negociação</dt>
              <dd>
                {{
                  tarefaSelecionada.oportunidade || "Sem oportunidade vinculada"
                }}
              </dd>
            </div>
            <div>
              <dt>Contato</dt>
              <dd>
                {{ tarefaSelecionada.contato || "Sem contato vinculado" }}
              </dd>
            </div>
            <div v-if="tarefaSelecionada.concluido_em">
              <dt>Conclusão</dt>
              <dd>{{ formatDateTime(tarefaSelecionada.concluido_em) }}</dd>
            </div>
          </dl>
          <section class="crm-task-description">
            <strong>Descrição</strong>
            <p>{{ tarefaSelecionada.descricao || "Sem descrição." }}</p>
          </section>
        </template>

        <button
          v-if="tarefaSelecionada.id_crm_oportunidade && !drawerEditando"
          class="btn btn-dark btn-block"
          type="button"
          @click="abrirOportunidade(tarefaSelecionada)"
        >
          Abrir oportunidade
        </button>
        <button
          v-if="canEditTarefa(tarefaSelecionada) && !drawerEditando"
          class="btn btn-success btn-block"
          type="button"
          @click="concluirTarefa(tarefaSelecionada)"
        >
          Concluir tarefa
        </button>
      </div>
    </b-sidebar>

    <b-modal
      id="crm-tarefa-modal"
      title="Nova tarefa"
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok.prevent="salvar"
    >
      <b-form-group label="Título">
        <b-form-input v-model="form.titulo" />
      </b-form-group>
      <b-form-group label="Tipo">
        <b-form-select v-model="form.tipo" :options="tipos" />
      </b-form-group>
      <b-form-group label="Vencimento">
        <b-form-input v-model="form.vencimento_em" type="datetime-local" />
      </b-form-group>
      <BuscaClienteContato
        v-model="vinculoSelecionado"
        label="Contato ou oportunidade"
        :incluir-pessoas="false"
        incluir-oportunidades
        @criar-contato="criarContatoRapido"
      />
      <b-form-group label="Descrição">
        <b-form-textarea v-model="form.descricao" rows="3" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import BuscaClienteContato from "@/modules/CRM/components/BuscaClienteContato";
import crmService from "@/modules/CRM/services/crmService";

export default {
  name: "crm-tarefas",
  components: { BuscaClienteContato },
  data() {
    return {
      buscaTimer: null,
      drawerEditando: false,
      drawerForm: this.emptyDrawerForm(),
      drawerVisible: false,
      form: this.emptyForm(),
      filtros: {
        busca: "",
        status: "todos",
      },
      loading: false,
      loadingDrawerSave: false,
      pagination: {
        page: 1,
        perPage: 10,
        total: 0,
      },
      perPageOptions: [
        { value: 10, text: "10" },
        { value: 20, text: "20" },
        { value: 30, text: "30" },
        { value: 50, text: "50" },
      ],
      statusFilterOptions: [
        { value: "todos", text: "Todos os status" },
        { value: "agendadas", text: "Agendadas" },
        { value: "atrasadas", text: "Atrasadas" },
        { value: "concluidas", text: "Concluídas" },
      ],
      tarefaSelecionada: null,
      tarefas: [],
      tiposTarefa: [],
      usuarios: [],
      vinculoSelecionado: null,
    };
  },
  computed: {
    lastPage() {
      return Math.max(
        1,
        Math.ceil(this.pagination.total / this.pagination.perPage)
      );
    },
    tipos() {
      const ativos = this.tiposTarefa.filter((tipo) => tipo.ativo !== false);
      const tipos = ativos.length
        ? ativos
        : [
            { nome: "Tarefa", chave: "tarefa" },
            { nome: "Ligação", chave: "ligacao" },
            { nome: "Reunião", chave: "reuniao" },
            { nome: "Nota", chave: "nota" },
            { nome: "Follow-up", chave: "follow_up" },
          ];

      return tipos.map((tipo) => ({
        value: tipo.chave,
        text: tipo.nome,
      }));
    },
    usuarioOptionsSemTodos() {
      return this.usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
  },
  mounted() {
    this.loadTiposTarefa();
    this.loadUsuarios();
    this.loadAtividades();
  },
  beforeDestroy() {
    clearTimeout(this.buscaTimer);
  },
  methods: {
    emptyForm() {
      return {
        titulo: "",
        tipo: "tarefa",
        vencimento_em: null,
        descricao: "",
        id_crm_oportunidade: null,
        id_crm_contato: null,
      };
    },
    emptyDrawerForm() {
      return {
        titulo: "",
        tipo: "tarefa",
        id_usuario: null,
        vencimento_em: null,
        descricao: "",
      };
    },
    atividadeParams() {
      const params = {
        busca: this.filtros.busca,
        page: this.pagination.page,
        per_page: this.pagination.perPage,
        sort: {
          field: "vencimento_em",
          type: "asc",
        },
      };

      if (this.filtros.status === "agendadas") {
        params.status = "aberta";
      }

      if (this.filtros.status === "atrasadas") {
        params.periodo = "atrasadas";
      }

      if (this.filtros.status === "concluidas") {
        params.status = "concluida";
      }

      return params;
    },
    async loadAtividades() {
      this.loading = true;

      try {
        const { data } = await crmService.atividades(this.atividadeParams());
        this.tarefas = this.normalizeCollection(data);
        this.pagination.total = Number(data?.total || this.tarefas.length || 0);
        this.pagination.page = Number(
          data?.current_page || this.pagination.page
        );
        this.pagination.perPage = Number(
          data?.per_page || this.pagination.perPage
        );
      } catch (error) {
        this.tarefas = [];
        this.pagination.total = 0;
      } finally {
        this.loading = false;
      }
    },
    async loadTiposTarefa() {
      const { data } = await crmService.tiposTarefaConfiguracao();
      this.tiposTarefa = this.normalizeCollection(data);
    },
    async loadUsuarios() {
      const { data } = await crmService.usuarios({
        per_page: 100,
        page: 1,
        sort: [{ field: "nome", type: "asc" }],
      });
      this.usuarios = this.normalizeCollection(data);
    },
    abrirDrawer(tarefa) {
      this.tarefaSelecionada = tarefa;
      this.drawerForm = this.formFromTarefa(tarefa);
      this.drawerEditando = false;
      this.drawerVisible = true;
    },
    abrirModal() {
      this.form = this.emptyForm();
      this.form.tipo = this.tipos.length ? this.tipos[0].value : "tarefa";
      this.vinculoSelecionado = null;
      this.$bvModal.show("crm-tarefa-modal");
    },
    cancelarEdicaoDrawer() {
      this.drawerForm = this.formFromTarefa(this.tarefaSelecionada);
      this.drawerEditando = false;
    },
    canEditTarefa(tarefa) {
      return tarefa && !this.isConcluida(tarefa);
    },
    async concluirTarefa(tarefa) {
      if (!this.canEditTarefa(tarefa)) {
        return;
      }

      await crmService.concluirAtividade({
        id_crm_atividade: tarefa.id_crm_atividade,
      });
      this.$toast.success("Tarefa concluída com sucesso.");
      this.drawerEditando = false;
      this.loadAtividades();
      this.tarefaSelecionada = {
        ...tarefa,
        status: "concluida",
        concluido_em: new Date().toISOString(),
      };
    },
    abrirOportunidade(tarefa) {
      if (!tarefa || !tarefa.id_crm_oportunidade) {
        return;
      }

      this.$router.push({
        name: "crmOportunidadeDetalhe",
        params: { id: tarefa.id_crm_oportunidade },
        query: { origem: "tarefas" },
      });
    },
    debouncedLoadAtividades() {
      clearTimeout(this.buscaTimer);
      this.buscaTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.loadAtividades();
      }, 350);
    },
    formatDateTime(value) {
      if (!value) {
        return "Sem data";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "Sem data";
      }

      const formatted = new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);

      return formatted.replace(",", " às");
    },
    formFromTarefa(tarefa) {
      if (!tarefa) {
        return this.emptyDrawerForm();
      }

      return {
        titulo: tarefa.titulo || "",
        tipo:
          tarefa.tipo || (this.tipos.length ? this.tipos[0].value : "tarefa"),
        id_usuario: tarefa.id_usuario || null,
        vencimento_em: this.toDateTimeLocal(tarefa.vencimento_em),
        descricao: tarefa.descricao || "",
      };
    },
    iniciarEdicaoDrawer() {
      this.drawerForm = this.formFromTarefa(this.tarefaSelecionada);
      this.drawerEditando = true;
    },
    isConcluida(tarefa) {
      return tarefa && tarefa.status === "concluida";
    },
    isLate(tarefa) {
      if (!tarefa || this.isConcluida(tarefa) || !tarefa.vencimento_em) {
        return false;
      }

      const date = new Date(tarefa.vencimento_em);

      return !Number.isNaN(date.getTime()) && date < new Date();
    },
    normalizeCollection(data) {
      if (Array.isArray(data)) {
        return data;
      }

      if (data && Array.isArray(data.data)) {
        return data.data;
      }

      return [];
    },
    responseData(response) {
      return response?.data?.data || response?.data || null;
    },
    async salvar() {
      if (!this.form.titulo) {
        this.$toast.error("Informe o título da tarefa.");
        return;
      }

      if (!this.vinculoSelecionado) {
        this.$toast.error("Selecione um contato ou oportunidade.");
        return;
      }

      await crmService.criarAtividade({
        ...this.form,
        id_crm_oportunidade: this.vinculoSelecionado.id_crm_oportunidade,
        id_crm_contato: this.vinculoSelecionado.id_crm_contato,
      });
      this.$toast.success("Tarefa criada com sucesso.");
      this.$bvModal.hide("crm-tarefa-modal");
      this.pagination.page = 1;
      this.loadAtividades();
    },
    async salvarEdicaoDrawer() {
      if (
        !this.tarefaSelecionada ||
        !this.canEditTarefa(this.tarefaSelecionada)
      ) {
        return;
      }

      if (!this.drawerForm.titulo) {
        this.$toast.error("Informe o título da tarefa.");
        return;
      }

      if (!this.drawerForm.id_usuario) {
        this.$toast.error("Informe o responsável da tarefa.");
        return;
      }

      this.loadingDrawerSave = true;

      try {
        const response = await crmService.atualizarAtividade({
          id_crm_atividade: this.tarefaSelecionada.id_crm_atividade,
          titulo: this.drawerForm.titulo,
          tipo: this.drawerForm.tipo,
          id_usuario: this.drawerForm.id_usuario,
          vencimento_em: this.toApiDateTime(this.drawerForm.vencimento_em),
          descricao: this.drawerForm.descricao,
        });
        const atividade = this.responseData(response);
        const tarefaAtualizada = this.mergeTarefaAtualizada(
          this.tarefaSelecionada,
          atividade
        );

        this.tarefaSelecionada = tarefaAtualizada;
        this.atualizarTarefaNaLista(tarefaAtualizada);
        this.drawerEditando = false;
        this.$toast.success("Tarefa atualizada com sucesso.");
        this.loadAtividades();
      } finally {
        this.loadingDrawerSave = false;
      }
    },
    async criarContatoRapido(payload) {
      const contatoPayload =
        typeof payload === "string"
          ? { nome: payload, origem_criacao_rapida: true }
          : { ...payload, origem_criacao_rapida: true };
      const { data } = await crmService.criarContato(contatoPayload);
      const contato = data.contato || data;
      this.vinculoSelecionado = {
        tipo: "contato",
        label: contato.nome,
        id_crm_contato: contato.id_crm_contato,
        id_pessoa: contato.id_pessoa || null,
        id_crm_oportunidade: null,
        raw: contato,
      };
      this.$toast.success("Contato criado e selecionado.");
    },
    statusClass(tarefa) {
      if (this.isConcluida(tarefa)) {
        return "is-done";
      }

      return this.isLate(tarefa) ? "is-late" : "is-scheduled";
    },
    statusTexto(tarefa) {
      if (this.isConcluida(tarefa)) {
        return "Concluída";
      }

      return this.isLate(tarefa) ? "Atrasada" : "Agendada";
    },
    toApiDateTime(value) {
      return value ? value.replace("T", " ") : null;
    },
    toDateTimeLocal(value) {
      if (!value) {
        return null;
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      const pad = (number) => String(number).padStart(2, "0");

      return (
        [
          date.getFullYear(),
          pad(date.getMonth() + 1),
          pad(date.getDate()),
        ].join("-") + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
      );
    },
    atualizarTarefaNaLista(tarefaAtualizada) {
      this.tarefas = this.tarefas.map((tarefa) =>
        tarefa.id_crm_atividade === tarefaAtualizada.id_crm_atividade
          ? tarefaAtualizada
          : tarefa
      );
    },
    mergeTarefaAtualizada(tarefaAtual, atividade) {
      const idUsuario = atividade?.id_usuario || this.drawerForm.id_usuario;
      const usuarioSelecionado = this.usuarios.find(
        (usuario) => Number(usuario.id_usuario) === Number(idUsuario)
      );

      return {
        ...tarefaAtual,
        ...(atividade || {}),
        id_usuario: idUsuario,
        titulo: atividade?.titulo || this.drawerForm.titulo,
        tipo: atividade?.tipo || this.drawerForm.tipo,
        descricao:
          atividade &&
          Object.prototype.hasOwnProperty.call(atividade, "descricao")
            ? atividade.descricao
            : this.drawerForm.descricao,
        vencimento_em:
          atividade?.vencimento_em ||
          this.toApiDateTime(this.drawerForm.vencimento_em),
        usuario:
          atividade?.usuario ||
          usuarioSelecionado?.nome ||
          usuarioSelecionado?.usuario ||
          tarefaAtual.usuario,
      };
    },
    tipoTexto(tipo) {
      const option = this.tipos.find((item) => item.value === tipo);

      return option ? option.text : tipo || "Tarefa";
    },
    trocarFiltroStatus() {
      this.pagination.page = 1;
      this.loadAtividades();
    },
    trocarPagina(page) {
      this.pagination.page = Math.min(Math.max(page, 1), this.lastPage);
      this.loadAtividades();
    },
    trocarPorPagina() {
      this.pagination.page = 1;
      this.loadAtividades();
    },
  },
};
</script>

<style scoped>
.crm-tasks-page {
  padding: 24px;
}

.crm-tasks-panel {
  background: #ffffff;
  border: 1px solid #eef1f5;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(23, 31, 56, 0.04);
  padding: 26px 30px;
}

.crm-tasks-header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 22px;
}

.crm-tasks-header h1 {
  color: #1f2940;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.crm-tasks-header span {
  color: #7e8798;
  display: block;
  font-size: 12px;
  margin-top: 4px;
}

.crm-tasks-filters {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(220px, 1fr) 220px auto;
  margin-bottom: 20px;
}

.crm-tasks-table-wrap {
  overflow-x: auto;
}

.crm-tasks-table {
  border-collapse: collapse;
  color: #3f4254;
  min-width: 900px;
  width: 100%;
}

.crm-tasks-table th {
  border-bottom: 1px solid #e8edf4;
  color: #a1a8b7;
  font-size: 12px;
  font-weight: 800;
  padding: 13px 10px;
  text-align: left;
}

.crm-tasks-table td {
  border-bottom: 1px solid #eef1f5;
  font-size: 13px;
  font-weight: 700;
  padding: 14px 10px;
  vertical-align: middle;
}

.crm-task-row {
  cursor: pointer;
  transition: background-color 0.14s ease;
}

.crm-task-row:hover {
  background: #f8fbff;
}

.crm-task-row.is-late {
  background: #fff8fa;
}

.crm-task-title-cell {
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
}

.crm-task-title-cell i {
  color: #0bb7d4;
  font-size: 18px;
}

.crm-task-title-cell strong,
.crm-task-title-cell span {
  display: block;
  overflow-wrap: anywhere;
}

.crm-task-title-cell span {
  color: #7e8798;
  font-size: 12px;
  margin-top: 2px;
}

.crm-task-status {
  border-radius: 4px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  padding: 7px 9px;
  text-transform: uppercase;
}

.crm-task-status.is-scheduled {
  background: #e8f8ff;
  color: #0478a8;
}

.crm-task-status.is-late {
  background: #fff2f5;
  color: #d63952;
}

.crm-task-status.is-done {
  background: #e8fff3;
  color: #0f7b4d;
}

.crm-task-link {
  background: transparent;
  border: 0;
  color: #0b87c9;
  font-weight: 800;
  padding: 0;
  text-align: left;
}

.crm-task-link:hover {
  color: #075c87;
  text-decoration: underline;
}

.crm-tasks-empty {
  color: #7e8798;
  font-weight: 600;
  height: 120px;
  text-align: center;
}

.crm-tasks-footer {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-top: 18px;
}

.crm-tasks-per-page {
  max-width: 90px;
}

.crm-tasks-pages {
  align-items: center;
  color: #6f7890;
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  gap: 10px;
}

.crm-task-drawer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 18px 22px;
}

.crm-task-drawer h2 {
  color: #1f2940;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0;
  overflow-wrap: anywhere;
}

.crm-task-drawer-top {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.crm-task-drawer-actions {
  display: inline-flex;
  gap: 8px;
}

.crm-task-drawer dl {
  display: grid;
  gap: 12px;
  margin: 0;
}

.crm-task-drawer dl > div {
  border-bottom: 1px solid #eef1f5;
  padding-bottom: 10px;
}

.crm-task-drawer dt {
  color: #8d96a8;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 4px;
}

.crm-task-drawer dd {
  color: #2f3850;
  font-size: 13px;
  font-weight: 700;
  margin: 0;
  overflow-wrap: anywhere;
}

.crm-task-description {
  background: #f7f9fc;
  border: 1px solid #e8edf4;
  border-radius: 8px;
  padding: 12px;
}

.crm-task-description strong {
  color: #2f3850;
  display: block;
  font-size: 12px;
  margin-bottom: 6px;
}

.crm-task-description p {
  color: #4b556c;
  font-size: 13px;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

@media (max-width: 767px) {
  .crm-tasks-page {
    padding: 14px;
  }

  .crm-tasks-panel {
    padding: 18px;
  }

  .crm-tasks-header {
    align-items: stretch;
    flex-direction: column;
  }

  .crm-tasks-filters {
    grid-template-columns: 1fr;
  }

  .crm-tasks-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .crm-tasks-pages {
    justify-content: space-between;
  }
}
</style>
